import {graphUrl} from '../../authConfig';
import {IPersonaProps, MessageBarType} from '@fluentui/react';
import {LoggerContext} from '../../providers';
import {useContext, useMemo} from 'react';
import {AdminService, UnitDto, UserService} from '../../services/generated';
import {DocumentInUnit, ProcessCommentManage, RolePermission} from '../../models';
import {useApiService} from '../../services/helpers';
import {ApiResponsePending} from '../../services/ApiResponseType';
import {LogMessageType} from '../../services/LogMessagesType';
import {AuthenticationContext} from '../../providers/AuthenticationContext';
import {AccountInfo} from '@azure/msal-common';
import {IGraphUser} from '../../interfaces';
import {MainStoreContext} from '../../providers/MainStoreContext';
import { MyTaskType } from '../../services/generated/models/MyTaskType';
import { MyTask } from '../../models/MyTask';
import { DocumentIdAngLanguageCodeDto } from '../../services/generated/models/DocumentIdAngLanguageCodeDto';

export const useUser = () => {
  const requestWrapper = useApiService();
  const {addLog} = useContext(LoggerContext);
  const {auth, acquireToken, logout} = useContext(AuthenticationContext);
  const {users, rolePermissions} = useContext(MainStoreContext);

  const scopes = useMemo(
    () => [
      'openid',
      'profile',
      'User.Read',
      'User.Read.All',
      'User.ReadBasic.All',
      'offline_access'
    ],
    []
  );

  /** get current user details */
  const getCurrentUser = async (): Promise<AccountInfo | null> => {
    return auth?.account || null;
  };

  /** get current user details if user is admin */
  const getCurrentUserAdminDetails = async (
    log: LogMessageType = {
      error: false
    }
  ): ApiResponsePending<boolean> => {
    return await requestWrapper(AdminService.getApiAdmin(), (result, apiCode) => !apiCode, log);
  };

    /** get current user details if user is admin */
    const getCurrentUserIsContributor = async (
      log: LogMessageType = {
        error: false
      }
    ): ApiResponsePending<boolean> => {
      return await requestWrapper(AdminService.getApiIsContributor(), (result, apiCode) => !apiCode, log);
    };

  const getUserPermissionsPage = async (
    unitId: number,
    processId?: number,
    force?: boolean,
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<RolePermission[]> => {
    const cachedRole = force
      ? null
      : rolePermissions.find(role => role.unitId === unitId && role.processId === processId);
    if (cachedRole) {
      return cachedRole.rolePermissions;
    }
    const response = await requestWrapper(
      UserService.readUserPermissions(unitId, processId),
      result => result.map(item => new RolePermission(item)),
      log
    );
    if (!response.apiCode) {
      const existing = rolePermissions.find(
        permission => unitId === permission.unitId && processId === permission.processId
      );
      if (existing) {
        existing.rolePermissions = response;
      } else {
        rolePermissions.push({
          unitId,
          processId,
          rolePermissions: response
        });
      }
    }
    return response;
  };

  const markAsFavorite = async (
      doc: DocumentIdAngLanguageCodeDto,
      log: LogMessageType = {
        error: true,
        success: true,
        successMessage: 'Document added to favorites successfully'
      }
    ): ApiResponsePending<boolean> => {
      return await requestWrapper(UserService.markAsFavorite(doc), (result, apiCode) => !apiCode, log);
    };
  
     const unMarkAsFavorite = async (
      doc: DocumentIdAngLanguageCodeDto,
      log: LogMessageType = {
        error: true,
        success: true,
        successMessage: 'Document removed from favorites successfully'
      }
    ): ApiResponsePending<boolean> => {
      return await requestWrapper(UserService.unMarkAsFavorite(doc), (result, apiCode) => !apiCode, log);
    };


     const getUserFavoriteDocumentsInUnit = async (
        unitId: number,
        log: LogMessageType = {
          error: true
        }
      ): ApiResponsePending<DocumentInUnit[]> => {
        return await requestWrapper(
          UserService.getApiUserFavoriteDocuments(unitId),
          result => result.map(item => new DocumentInUnit(item)),
          log
        );
      };

  const getUserMyTasks = async (
    type: MyTaskType,
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<MyTask[]> => {
    return requestWrapper(UserService.getApiMyTasks(type), result => result.map(item =>new MyTask(item)), log);
  }

    const getUserHasAccessToAdditionalTabs = async (
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<boolean> => {
    return requestWrapper(UserService.getUserHasAccessToAdditionalTabs(), result => result, log);
  }

  const getUserMyTasksCount = async (
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<number> => {
    return requestWrapper(UserService.getApiMyTasksCount(), result => result, log);
  }

  const getUserCommentsInUnit = async (
      unitId: number,
      log: LogMessageType = {
        error: true
      }
    ): ApiResponsePending<ProcessCommentManage[]> => {
      return await requestWrapper(
        UserService.getApiUserCommentsInUnit(unitId),
        result => result.map(item => new ProcessCommentManage(item)),
        log
      );
    };


  const getUserIsPortalOwner = async (
    log: LogMessageType = {
      error: true
    }
  ): ApiResponsePending<UnitDto[]> => {
    return requestWrapper(UserService.readUserIsPortalOwner(), result => result, log);
  };

  const getUserById = async (userId: string): Promise<IGraphUser | undefined> => {
    const userToken = await acquireToken(scopes);
    try {
      if (!users.cacheById[userId]) {
        users.cacheById[userId] = fetch(`${graphUrl}/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          method: 'GET'
        }).then(response => response.json());
      }
      return users.cacheById[userId];
    } catch (error) {
      addLog(MessageBarType.error, undefined, error);
      return undefined;
    }
  };

  const getAllUsersList = async (): Promise<IPersonaProps[]> => {
    const userToken = await acquireToken(scopes);
    try {
      const response = await fetch(`${graphUrl}/users?$top=100`, {
        headers: {
          Authorization: `Bearer ${userToken}`
        },
        method: 'GET'
      });
      const resp = await response.json();
      return resp;
    } catch (error) {
      addLog(MessageBarType.error, undefined, error);
      return [];
    }
  };

  const getFilteredUsersList = async (filter?: string): Promise<IPersonaProps[]> => {
    const userToken = await acquireToken(scopes);
    if (!filter || filter.length < 2) {
      return [];
    }

    try {
      const response = await fetch(
        `${graphUrl}/users?$filter=userType ne 'Guest'
        AND endsWith(mail, 'abb.com')
        AND accountEnabled eq true&$search="displayName:${filter}" OR "mail:${filter}"`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            ConsistencyLevel: 'eventual'
          },
          method: 'GET'
        }
      );
      if (response.ok) {
        let result = await response.json();

        const people: IPersonaProps[] = result.value.map(
          (item: {id: number; displayName: string; mail: string; userPrincipalName: string}) => {
            return {
              id: item.id,
              text: item.displayName,
              secondaryText: item.mail,
              key: item.id,
              userPrincipalName: item.userPrincipalName
            };
          }
        );
        return people;
      } else {
        return [];
      }
    } catch (error) {
      addLog(MessageBarType.error, undefined, error);
      return [];
    }
  };

  return useMemo(
    () => ({
      acquireToken,
      getUserCommentsInUnit,
      getUserMyTasksCount,
      getUserMyTasks,
      getCurrentUserIsContributor,
      getCurrentUser,
      getCurrentUserAdminDetails,
      getUserPermissionsPage,
      getUserHasAccessToAdditionalTabs,
      getUserById,
      getAllUsersList,
      markAsFavorite,
      getUserFavoriteDocumentsInUnit,
      unMarkAsFavorite,
      getFilteredUsersList,
      getUserIsPortalOwner,
      logout
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};
